import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { loadTextOverrides, navigate, useLocation, useToast, useTRPC } from '@barev/web';
import { useEffect, useMemo, useState } from 'react';
import type { Me } from '../App.tsx';
import { errText, useT } from '../i18n.tsx';
import { Dialog, Field, I, Skeleton } from '../components/kit.tsx';

type Tab = 'services' | 'plans' | 'promos' | 'relocation';
const LANGS = ['ru', 'en', 'hy', 'zh', 'es'] as const;
type Lang = (typeof LANGS)[number];
type L5 = Record<Lang, string>;
const EMPTY: L5 = { ru: '', en: '', hy: '', zh: '', es: '' };
const CATS = ['HOME', 'DOCS', 'LEARNING', 'PERKS'] as const;
const num = (v: string) => v.replace(/\D/g, '');
const hhmm = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
const toMin = (s: string) => {
  const [h, m] = s.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};

export function Products({ me }: { me: Me }) {
  const { t } = useT();
  const { query } = useLocation();
  const tab = (query.get('tab') as Tab) || 'services';
  const canEdit = me.roles.includes('SUPERADMIN');
  return (
    <>
      <div className="bo-head">
        <h1 className="bo-title">{t('pr.title')}</h1>
      </div>
      <div className="bo-ogroups" role="tablist">
        {(['services', 'plans', 'promos', 'relocation'] as const).map((x) => (
          <button key={x} type="button" role="tab" className="bo-ogroup" aria-selected={tab === x} onClick={() => navigate(x === 'services' ? '/products' : `/products?tab=${x}`, { replace: true })}>
            {t(`pr.tab.${x}`)}
          </button>
        ))}
      </div>
      {!canEdit && <p className="bo-hint" style={{ marginBottom: 12 }}>{t('pr.readOnly')}</p>}
      {tab === 'services' && <Services canEdit={canEdit} />}
      {tab === 'plans' && <Plans canEdit={canEdit} />}
      {tab === 'promos' && <Promos canEdit={canEdit} />}
      {tab === 'relocation' && <Relocation canEdit={canEdit} />}
    </>
  );
}

// ───── услуги ─────

function Services({ canEdit }: { canEdit: boolean }) {
  const { t, shared, amd } = useT();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const cat = useQuery(trpc.bo.products.catalog.queryOptions());
  const [creating, setCreating] = useState(false);
  const move = useMutation(trpc.bo.products.moveService.mutationOptions({ onSuccess: () => qc.invalidateQueries({ queryKey: trpc.bo.products.catalog.queryKey() }) }));
  if (!cat.data) return <Skeleton h={300} />;
  return (
    <div className="bo-col" style={{ gap: 18 }}>
      {canEdit && (
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <button type="button" className="bo-btn bo-btn--accent" onClick={() => setCreating(true)}>
            <I name="plus" size={16} />
            {t('pr.newService')}
          </button>
          <span className="bo-hint">{t('pr.newServiceHint')}</span>
        </div>
      )}
      {creating && <NewServiceDialog catalog={cat.data} onClose={() => setCreating(false)} />}
      {cat.data.map((c) => (
        <section key={c.id} className="bo-panel">
          <div className="bo-panel__head">
            <h2 className="bo-h2">{shared(`categories.${c.code}.name`)}</h2>
            <span className="bo-small bo-muted">{t('pr.catDiscounts', { plus: c.barevPlusDiscountPct, elite: c.eliteDiscountPct })}</span>
          </div>
          <ul className="bo-list">
            {c.services.map((s) => (
              <li key={s.id} style={{ opacity: s.active ? 1 : 0.55 }}>
                {canEdit && (
                  <span className="bo-stages__arrows">
                    <button type="button" onClick={() => move.mutate({ id: s.id, dir: -1 })} aria-label="↑">
                      <I name="arrowUp" size={14} />
                    </button>
                    <button type="button" onClick={() => move.mutate({ id: s.id, dir: 1 })} aria-label="↓">
                      <I name="arrowDown" size={14} />
                    </button>
                  </span>
                )}
                <a href={`#/products/${s.id}`} className="bo-grow" style={{ color: 'inherit', textDecoration: 'none' }}>
                  <b>{shared(`services.${s.code}.name`)}</b>
                  <span className="bo-small bo-muted" style={{ display: 'block' }}>
                    {[s.active ? t('pr.on') : t('pr.off'), t('pr.from', { sum: amd(s.fromPrice) }), shared(`roles.${s.providerRole}`), t('pr.orders30', { n: s.orders30 })].join(' · ')}
                  </span>
                </a>
                <a className="bo-btn bo-btn--ghost bo-btn--sm" href={`#/products/${s.id}`}>
                  {t('pr.setup')}
                </a>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

/** Поля на 5 языках со стандартным текстом в подсказке */
function L5Editor({ label, value, defaults, onChange, multiline, disabled }: { label: string; value: L5; defaults: Partial<L5>; onChange: (v: L5) => void; multiline?: boolean; disabled?: boolean }) {
  return (
    <div className="bo-field">
      <span className="bo-label">{label}</span>
      <div className="bo-l5">
        {LANGS.map((l) => (
          <label key={l} className="bo-l5__row">
            <span className="bo-l5__lang">{l.toUpperCase()}</span>
            {multiline ? (
              <textarea className="bo-textarea" style={{ minHeight: 60 }} disabled={disabled} value={value[l]} placeholder={defaults[l] ?? ''} onChange={(e) => onChange({ ...value, [l]: e.target.value })} />
            ) : (
              <input className="bo-input" disabled={disabled} value={value[l]} placeholder={defaults[l] ?? ''} onChange={(e) => onChange({ ...value, [l]: e.target.value })} />
            )}
          </label>
        ))}
      </div>
    </div>
  );
}

type Catalog = NonNullable<ReturnType<typeof useCatalog>>;
function useCatalog() {
  const trpc = useTRPC();
  return useQuery(trpc.bo.products.catalog.queryOptions()).data;
}

export function ServiceEditor({ id, me }: { id: string; me: Me }) {
  const { t, shared } = useT();
  const cat = useCatalog();
  const canEdit = me.roles.includes('SUPERADMIN');
  const found = useMemo(() => {
    for (const c of cat ?? []) for (const s of c.services) if (s.id === id) return { c, s };
    return null;
  }, [cat, id]);
  const back = (
    <a className="bo-back" href="#/products">
      <I name="back" size={16} />
      {t('pr.back')}
    </a>
  );
  if (!cat) return <Skeleton h={300} />;
  if (!found) return back;
  const { c, s } = found;
  return (
    <>
      {back}
      <div className="bo-head">
        <div>
          <h1 className="bo-title">{shared(`services.${s.code}.name`)}</h1>
          <p className="bo-sub">{[shared(`categories.${c.code}.name`), shared(`roles.${s.providerRole}`), s.isOnline ? shared('common.online') : null].filter(Boolean).join(' · ')}</p>
        </div>
      </div>
      <div className="bo-client-grid">
        <div className="bo-col">
          <ContentPanel code={s.code} canEdit={canEdit} extras={s.pricing.kind === 'rooms' ? Object.keys(s.pricing.extras) : []} />
          {s.pricing.kind === 'fixed' && <ParamsPanel service={s} canEdit={canEdit} />}
        </div>
        <div className="bo-col">
          <SettingsPanel service={s} canEdit={canEdit} />
          <CategoryPanel category={c} canEdit={canEdit} />
          <ChecklistPanel service={s} canEdit={canEdit} />
        </div>
      </div>
      <div style={{ marginTop: 16 }}>
        <PricePanel service={s} canEdit={canEdit} />
      </div>
      {canEdit && <DeleteService id={s.id} />}
    </>
  );
}

function useSaved() {
  const { t } = useT();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const toast = useToast();
  return {
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: trpc.bo.products.catalog.queryKey() });
      qc.invalidateQueries({ queryKey: trpc.bo.products.texts.queryKey() });
      void loadTextOverrides();
      toast(t('prv.saved'));
    },
    onError: (e: unknown) => toast(errText(t, e), 'error'),
  };
}

/** Наполнение: название и описание услуги, названия доп. услуг */
function ContentPanel({ code, canEdit, extras }: { code: string; canEdit: boolean; extras: string[] }) {
  const { t, shared } = useT();
  const trpc = useTRPC();
  const keys = [`services.${code}.name`, `services.${code}.description`, ...extras.map((x) => `extras.${x}.name`)];
  const texts = useQuery(trpc.bo.products.texts.queryOptions({ keys }));
  const [draft, setDraft] = useState<Record<string, L5> | null>(null);
  useEffect(() => {
    if (texts.data && !draft) setDraft(Object.fromEntries(keys.map((k) => [k, Object.fromEntries(LANGS.map((l) => [l, texts.data[k]?.[l]?.value ?? ''])) as L5])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [texts.data]);
  const save = useMutation(trpc.bo.products.saveTexts.mutationOptions(useSaved()));
  const defaults = (k: string) => Object.fromEntries(LANGS.map((l) => [l, texts.data?.[k]?.[l]?.default ?? ''])) as L5;
  const [showExtras, setShowExtras] = useState(false);
  if (!draft) return <Skeleton h={200} />;
  return (
    <section className="bo-panel">
      <div className="bo-panel__head">
        <h2 className="bo-h2">{t('pr.content')}</h2>
      </div>
      <p className="bo-hint" style={{ marginBottom: 12 }}>
        {t('pr.contentHint')}
      </p>
      <div className="bo-col" style={{ gap: 14 }}>
        <L5Editor label={t('pr.name')} value={draft[keys[0]!]!} defaults={defaults(keys[0]!)} onChange={(v) => setDraft({ ...draft, [keys[0]!]: v })} disabled={!canEdit} />
        <L5Editor label={t('pr.description')} value={draft[keys[1]!]!} defaults={defaults(keys[1]!)} onChange={(v) => setDraft({ ...draft, [keys[1]!]: v })} multiline disabled={!canEdit} />
        {extras.length > 0 && (
          <button type="button" className="bo-side__link" style={{ color: 'var(--bc-accent)', padding: 0, alignSelf: 'flex-start' }} onClick={() => setShowExtras((x) => !x)}>
            {t('pr.extrasNames', { n: extras.length })} {showExtras ? '▴' : '▾'}
          </button>
        )}
        {showExtras &&
          extras.map((x) => {
            const k = `extras.${x}.name`;
            return <L5Editor key={k} label={shared(k)} value={draft[k]!} defaults={defaults(k)} onChange={(v) => setDraft({ ...draft, [k]: v })} disabled={!canEdit} />;
          })}
      </div>
      {canEdit && (
        <button
          type="button"
          className="bo-btn bo-btn--sm"
          style={{ marginTop: 14 }}
          disabled={save.isPending}
          onClick={() => save.mutate(keys.flatMap((k) => LANGS.map((l) => ({ key: k, locale: l, value: draft[k]![l] }))))}
        >
          {t('ui.save')}
        </button>
      )}
    </section>
  );
}

type Service = Catalog[number]['services'][number];

/** Цена: тарифы по варианту (комнаты, санузлы) и доп. услуги — или фиксированная цена */
function PricePanel({ service: s, canEdit }: { service: Service; canEdit: boolean }) {
  const { t, shared } = useT();
  const trpc = useTRPC();
  const [p, setP] = useState(s.pricing);
  useEffect(() => setP(s.pricing), [s.pricing]);
  const save = useMutation(trpc.bo.products.saveService.mutationOptions(useSaved()));
  const unitLabel = (u: string) => t(`pr.unit.${u}`);
  const variantLabel = (v: string) => {
    if (p.kind !== 'rooms') return v;
    const k = `params.${s.code}.${p.variantParam}.${v}`;
    const r = shared(k);
    return r === k ? v : r;
  };
  const n = (v: number, on: (x: number) => void) => <input className="bo-input bo-num" inputMode="numeric" disabled={!canEdit} value={String(v)} onChange={(e) => on(Number(num(e.target.value)) || 0)} />;
  return (
    <section className="bo-panel">
      <div className="bo-panel__head">
        <h2 className="bo-h2">{t('pr.price')}</h2>
      </div>
      {p.kind === 'fixed' ? (
        <div className="bo-row2">
          <Field label={t('pr.fixedPrice')}>{n(p.price, (x) => setP({ ...p, price: x }))}</Field>
          <Field label={t('pr.minutes')}>{n(p.minutes, (x) => setP({ ...p, minutes: Math.max(1, x) }))}</Field>
        </div>
      ) : (
        <>
          <div className="bo-tablewrap">
            <table className="bo-table bo-ptab">
              <thead>
                <tr>
                  <th />
                  <th>{t('pr.base')}</th>
                  <th>{t('pr.perRoom')}</th>
                  <th>{t('pr.perBath')}</th>
                  <th>{t('pr.baseMin')}</th>
                  <th>{t('pr.perRoomMin')}</th>
                  <th>{t('pr.perBathMin')}</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(p.tiers).map(([v, tier]) => {
                  const set = (patch: Partial<typeof tier>) => setP({ ...p, tiers: { ...p.tiers, [v]: { ...tier, ...patch } } });
                  return (
                    <tr key={v}>
                      <td>
                        <b>{variantLabel(v)}</b>
                      </td>
                      <td>{n(tier.base, (x) => set({ base: x }))}</td>
                      <td>{n(tier.perRoom, (x) => set({ perRoom: x }))}</td>
                      <td>{n(tier.perBathroom, (x) => set({ perBathroom: x }))}</td>
                      <td>{n(tier.baseMinutes, (x) => set({ baseMinutes: Math.max(1, x) }))}</td>
                      <td>{n(tier.perRoomMinutes, (x) => set({ perRoomMinutes: x }))}</td>
                      <td>{n(tier.perBathroomMinutes, (x) => set({ perBathroomMinutes: x }))}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="bo-hint" style={{ margin: '8px 0 16px' }}>
            {t('pr.roomsHint')}
          </p>
          <h3 className="bo-h3" style={{ marginBottom: 8 }}>
            {t('pr.extras')}
          </h3>
          <div className="bo-tablewrap">
            <table className="bo-table bo-ptab">
              <thead>
                <tr>
                  <th />
                  <th>{t('pr.unitCol')}</th>
                  <th>{t('pr.extraPrice')}</th>
                  <th>{t('pr.extraMin')}</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(p.extras).map(([code, ex]) => {
                  const set = (patch: Partial<typeof ex>) => setP({ ...p, extras: { ...p.extras, [code]: { ...ex, ...patch } } });
                  return (
                    <tr key={code}>
                      <td>{shared(`extras.${code}.name`)}</td>
                      <td className="bo-small bo-muted">{unitLabel(ex.unit)}</td>
                      <td>{n(ex.price, (x) => set({ price: x }))}</td>
                      <td>{n(ex.minutes, (x) => set({ minutes: x }))}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
      {canEdit && (
        <button type="button" className="bo-btn bo-btn--sm" style={{ marginTop: 14 }} disabled={save.isPending} onClick={() => save.mutate({ id: s.id, active: s.active, lateCancelFeePct: s.lateCancelFeePct, pricing: p, slotConfig: s.slotConfig })}>
          {t('ui.save')}
        </button>
      )}
      <p className="bo-hint" style={{ marginTop: 10 }}>
        {t('pr.priceHint')}
      </p>
    </section>
  );
}

/** Включена ли услуга, окно записи, удержание за позднюю отмену */
function SettingsPanel({ service: s, canEdit }: { service: Service; canEdit: boolean }) {
  const { t } = useT();
  const trpc = useTRPC();
  const [f, setF] = useState({ active: s.active, fee: String(s.lateCancelFeePct), from: hhmm(s.slotConfig.dayStartMinute), to: hhmm(s.slotConfig.dayEndMinute), step: String(s.slotConfig.stepMinutes), lead: String(s.slotConfig.leadHours), horizon: String(s.slotConfig.horizonDays) });
  const save = useMutation(trpc.bo.products.saveService.mutationOptions(useSaved()));
  return (
    <section className="bo-panel">
      <div className="bo-panel__head">
        <h2 className="bo-h2">{t('pr.settings')}</h2>
      </div>
      <div className="bo-col" style={{ gap: 12 }}>
        <label className="bo-check">
          <input type="checkbox" disabled={!canEdit} checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} />
          {t('pr.activeLabel')}
        </label>
        <div className="bo-row2">
          <Field label={t('pr.windowFrom')}>
            <input className="bo-input" type="time" step={900} disabled={!canEdit} value={f.from} onChange={(e) => setF({ ...f, from: e.target.value })} />
          </Field>
          <Field label={t('pr.windowTo')}>
            <input className="bo-input" type="time" step={900} disabled={!canEdit} value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} />
          </Field>
        </div>
        <div className="bo-row2">
          <Field label={t('pr.step')}>
            <input className="bo-input" inputMode="numeric" disabled={!canEdit} value={f.step} onChange={(e) => setF({ ...f, step: num(e.target.value) })} />
          </Field>
          <Field label={t('pr.lead')}>
            <input className="bo-input" inputMode="numeric" disabled={!canEdit} value={f.lead} onChange={(e) => setF({ ...f, lead: num(e.target.value) })} />
          </Field>
        </div>
        <div className="bo-row2">
          <Field label={t('pr.horizon')}>
            <input className="bo-input" inputMode="numeric" disabled={!canEdit} value={f.horizon} onChange={(e) => setF({ ...f, horizon: num(e.target.value) })} />
          </Field>
          <Field label={t('pr.lateFee')}>
            <input className="bo-input" inputMode="numeric" disabled={!canEdit} value={f.fee} onChange={(e) => setF({ ...f, fee: num(e.target.value) })} />
          </Field>
        </div>
      </div>
      {canEdit && (
        <button
          type="button"
          className="bo-btn bo-btn--sm"
          style={{ marginTop: 14 }}
          disabled={save.isPending}
          onClick={() =>
            save.mutate({
              id: s.id,
              active: f.active,
              lateCancelFeePct: Math.min(100, Number(f.fee) || 0),
              pricing: s.pricing,
              slotConfig: { dayStartMinute: toMin(f.from), dayEndMinute: toMin(f.to), stepMinutes: Math.max(15, Number(f.step) || 30), leadHours: Number(f.lead) || 0, horizonDays: Math.max(1, Number(f.horizon) || 14) },
            })
          }
        >
          {t('ui.save')}
        </button>
      )}
    </section>
  );
}

/** Скидки по уровню подписки — на всё направление */
function CategoryPanel({ category: c, canEdit }: { category: Catalog[number]; canEdit: boolean }) {
  const { t, shared } = useT();
  const trpc = useTRPC();
  const [plus, setPlus] = useState(String(c.barevPlusDiscountPct));
  const [elite, setElite] = useState(String(c.eliteDiscountPct));
  const save = useMutation(trpc.bo.products.saveCategory.mutationOptions(useSaved()));
  return (
    <section className="bo-panel">
      <div className="bo-panel__head">
        <h2 className="bo-h2">{t('pr.discounts')}</h2>
      </div>
      <p className="bo-hint" style={{ marginBottom: 10 }}>
        {t('pr.discountsHint', { cat: shared(`categories.${c.code}.name`) })}
      </p>
      <div className="bo-row2">
        <Field label="Barev+, %">
          <input className="bo-input" inputMode="numeric" disabled={!canEdit} value={plus} onChange={(e) => setPlus(num(e.target.value))} />
        </Field>
        <Field label="Barev Elite, %">
          <input className="bo-input" inputMode="numeric" disabled={!canEdit} value={elite} onChange={(e) => setElite(num(e.target.value))} />
        </Field>
      </div>
      {canEdit && (
        <button type="button" className="bo-btn bo-btn--sm" style={{ marginTop: 12 }} disabled={save.isPending} onClick={() => save.mutate({ id: c.id, barevPlusDiscountPct: Math.min(100, Number(plus) || 0), eliteDiscountPct: Math.min(100, Number(elite) || 0) })}>
          {t('ui.save')}
        </button>
      )}
    </section>
  );
}

/** Чек-лист исполнителя: пункты и где нужно фото */
function ChecklistPanel({ service: s, canEdit }: { service: Service; canEdit: boolean }) {
  const { t, shared } = useT();
  const trpc = useTRPC();
  const [items, setItems] = useState<{ code?: string; label?: string; requiresPhoto: boolean }[]>(s.checklist);
  useEffect(() => setItems(s.checklist), [s.checklist]);
  const [label, setLabel] = useState('');
  const save = useMutation(trpc.bo.products.saveChecklist.mutationOptions(useSaved()));
  return (
    <section className="bo-panel">
      <div className="bo-panel__head">
        <h2 className="bo-h2">{t('pr.checklist')}</h2>
      </div>
      <ul className="bo-list">
        {items.map((x, i) => (
          <li key={x.code ?? `new-${i}`}>
            <span className="bo-grow">{x.code ? shared(`checklist.${x.code}`) : x.label}</span>
            <label className="bo-check bo-small">
              <input type="checkbox" disabled={!canEdit} checked={x.requiresPhoto} onChange={(e) => setItems(items.map((y, k) => (k === i ? { ...y, requiresPhoto: e.target.checked } : y)))} />
              {t('pr.photo')}
            </label>
            {canEdit && (
              <button type="button" className="bo-iconbtn" onClick={() => setItems(items.filter((_, k) => k !== i))} aria-label={t('chat.tplDelete')}>
                <I name="close" size={16} />
              </button>
            )}
          </li>
        ))}
      </ul>
      {canEdit && (
        <>
          <form
            style={{ display: 'flex', gap: 8, marginTop: 10 }}
            onSubmit={(e) => {
              e.preventDefault();
              if (!label.trim()) return;
              setItems([...items, { label: label.trim(), requiresPhoto: false }]);
              setLabel('');
            }}
          >
            <input className="bo-input" value={label} maxLength={120} onChange={(e) => setLabel(e.target.value)} placeholder={t('pr.newItem')} style={{ minHeight: 36, padding: '6px 12px' }} />
            <button type="submit" className="bo-btn bo-btn--ghost bo-btn--sm" disabled={!label.trim()}>
              <I name="plus" size={16} />
            </button>
          </form>
          <button type="button" className="bo-btn bo-btn--sm" style={{ marginTop: 12 }} disabled={save.isPending} onClick={() => save.mutate({ serviceId: s.id, items })}>
            {t('ui.save')}
          </button>
        </>
      )}
      <p className="bo-hint" style={{ marginTop: 10 }}>
        {t('pr.checklistHint')}
      </p>
    </section>
  );
}

// ───── подписка ─────

const PLAN_KEYS = ['barevPlus.monthlyPrice', 'barevPlus.maxDiscountPct', 'barevPlus.giftDays', 'elite.monthlyPrice', 'elite.maxDiscountPct'] as const;

function Plans({ canEdit }: { canEdit: boolean }) {
  const { t, shared } = useT();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const toast = useToast();
  const settings = useQuery({ ...trpc.bo.settings.get.queryOptions(), enabled: canEdit });
  const cat = useCatalog();
  const [f, setF] = useState<Record<string, string> | null>(null);
  useEffect(() => {
    if (settings.data && !f) setF(Object.fromEntries(PLAN_KEYS.map((k) => [k, String(settings.data[k])])));
  }, [settings.data, f]);
  const save = useMutation(
    trpc.bo.settings.save.mutationOptions({
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: trpc.bo.settings.get.queryKey() });
        toast(t('prv.saved'));
      },
      onError: (e) => toast(errText(t, e), 'error'),
    }),
  );
  return (
    <div className="bo-client-grid">
      <section className="bo-panel">
        <div className="bo-panel__head">
          <h2 className="bo-h2">{t('pr.plans')}</h2>
        </div>
        {!canEdit ? (
          <p className="bo-hint">{t('pr.plansSuper')}</p>
        ) : !f ? (
          <Skeleton h={200} />
        ) : (
          <>
            <div className="bo-col" style={{ gap: 12 }}>
              {PLAN_KEYS.map((k) => (
                <Field key={k} label={shared(`admin.settings.${k.replace('.', '_')}`)}>
                  <input className="bo-input" inputMode="numeric" value={f[k]} onChange={(e) => setF({ ...f, [k]: num(e.target.value) })} />
                </Field>
              ))}
            </div>
            <button type="button" className="bo-btn bo-btn--sm" style={{ marginTop: 14 }} disabled={save.isPending} onClick={() => save.mutate(Object.fromEntries(PLAN_KEYS.map((k) => [k, Number(f[k]) || 0])))}>
              {t('ui.save')}
            </button>
            <p className="bo-hint" style={{ marginTop: 10 }}>
              {t('pr.plansHint')}
            </p>
          </>
        )}
      </section>
      <section className="bo-panel">
        <div className="bo-panel__head">
          <h2 className="bo-h2">{t('pr.discountTable')}</h2>
        </div>
        {!cat ? (
          <Skeleton h={160} />
        ) : (
          <table className="bo-table">
            <thead>
              <tr>
                <th />
                <th className="bo-r">Barev+</th>
                <th className="bo-r">Elite</th>
              </tr>
            </thead>
            <tbody>
              {cat.map((c) => (
                <tr key={c.id}>
                  <td>{shared(`categories.${c.code}.name`)}</td>
                  <td className="bo-r bo-num">−{c.barevPlusDiscountPct}%</td>
                  <td className="bo-r bo-num">−{c.eliteDiscountPct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="bo-hint" style={{ marginTop: 10 }}>
          {t('pr.discountTableHint')}
        </p>
      </section>
    </div>
  );
}

// ───── промокоды ─────

type Promo = { id: string; code: string; discountPct: number; maxUses: number | null; usedCount: number; validFrom: Date | null; validUntil: Date | null; categories: string[]; note: string | null; active: boolean; state: string };

function Promos({ canEdit }: { canEdit: boolean }) {
  const { t, date, shared } = useT();
  const trpc = useTRPC();
  const list = useQuery(trpc.bo.products.promos.queryOptions());
  const [edit, setEdit] = useState<Promo | 'new' | null>(null);
  return (
    <section className="bo-panel">
      <div className="bo-panel__head">
        <p className="bo-hint" style={{ margin: 0, maxWidth: 560 }}>
          {t('pr.promoHint')}
        </p>
        {canEdit && (
          <button type="button" className="bo-btn bo-btn--sm" onClick={() => setEdit('new')}>
            <I name="plus" size={16} />
            {t('pr.newPromo')}
          </button>
        )}
      </div>
      {!list.data ? (
        <Skeleton h={160} />
      ) : list.data.length === 0 ? (
        <p className="bo-empty">{t('pr.noPromos')}</p>
      ) : (
        <ul className="bo-list">
          {list.data.map((p) => (
            <li key={p.id}>
              <span className="bo-promo">{p.code}</span>
              <span className="bo-grow" style={{ minWidth: 0 }}>
                <b className="bo-num">−{p.discountPct}%</b>
                <span className="bo-small bo-muted">
                  {' '}
                  · {t('pr.used', { n: p.usedCount, max: p.maxUses ?? '∞' })}
                  {p.validUntil && ` · ${t('pr.until', { date: date(p.validUntil) })}`}
                  {p.categories.length > 0 && ` · ${p.categories.map((c) => shared(`categories.${c}.name`)).join(', ')}`}
                  {p.note && ` · ${p.note}`}
                </span>
              </span>
              <span className={`bo-chip${p.state === 'live' ? ' bo-chip--ok' : ''}`}>{t(`pr.state.${p.state}`)}</span>
              {canEdit && (
                <button type="button" className="bo-btn bo-btn--ghost bo-btn--sm" onClick={() => setEdit(p)}>
                  {t('prv.edit')}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {edit && <PromoDialog promo={edit === 'new' ? undefined : edit} onClose={() => setEdit(null)} />}
    </section>
  );
}

function PromoDialog({ promo, onClose }: { promo?: Promo; onClose: () => void }) {
  const { t, shared } = useT();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const toast = useToast();
  const day = (d: Date | null) => (d ? new Date(d.getTime() + 4 * 3600_000).toISOString().slice(0, 10) : '');
  const [f, setF] = useState({
    code: promo?.code ?? '',
    pct: String(promo?.discountPct ?? 10),
    max: promo?.maxUses != null ? String(promo.maxUses) : '',
    from: day(promo?.validFrom ?? null),
    until: day(promo?.validUntil ?? null),
    categories: (promo?.categories ?? []) as (typeof CATS)[number][],
    note: promo?.note ?? '',
    active: promo?.active ?? true,
  });
  const save = useMutation(
    trpc.bo.products.savePromo.mutationOptions({
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: trpc.bo.products.promos.queryKey() });
        toast(t('prv.saved'));
        onClose();
      },
      onError: (e) => toast(errText(t, e), 'error'),
    }),
  );
  return (
    <Dialog
      open
      onClose={onClose}
      title={promo ? t('pr.promo') : t('pr.newPromo')}
      footer={
        <>
          <button type="button" className="bo-btn bo-btn--ghost" onClick={onClose}>
            {t('ui.close')}
          </button>
          <button
            type="button"
            className="bo-btn"
            disabled={f.code.trim().length < 3 || !Number(f.pct) || save.isPending}
            onClick={() =>
              save.mutate({
                id: promo?.id,
                code: f.code,
                discountPct: Math.min(100, Number(f.pct)),
                maxUses: f.max ? Number(f.max) : null,
                validFrom: f.from ? new Date(`${f.from}T00:00:00+04:00`) : null,
                validUntil: f.until ? new Date(`${f.until}T23:59:59+04:00`) : null,
                categories: f.categories,
                note: f.note || null,
                active: f.active,
              })
            }
          >
            {t('ui.save')}
          </button>
        </>
      }
    >
      <div className="bo-row2">
        <Field label={t('pr.code')}>
          <input className="bo-input" value={f.code} maxLength={40} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, '') })} placeholder="AUTUMN15" />
        </Field>
        <Field label={t('pr.pct')}>
          <input className="bo-input" inputMode="numeric" value={f.pct} onChange={(e) => setF({ ...f, pct: num(e.target.value) })} />
        </Field>
      </div>
      <div className="bo-row2">
        <Field label={t('pr.validFrom')}>
          <input className="bo-input" type="date" value={f.from} onChange={(e) => setF({ ...f, from: e.target.value })} />
        </Field>
        <Field label={t('pr.validUntil')}>
          <input className="bo-input" type="date" value={f.until} onChange={(e) => setF({ ...f, until: e.target.value })} />
        </Field>
      </div>
      <Field label={t('pr.maxUses')} hint={t('pr.maxUsesHint')}>
        <input className="bo-input" inputMode="numeric" value={f.max} onChange={(e) => setF({ ...f, max: num(e.target.value) })} placeholder="∞" />
      </Field>
      <div>
        <span className="bo-label">{t('pr.forCats')}</span>
        <div className="bo-pills" style={{ marginTop: 6 }}>
          {CATS.map((c) => (
            <button key={c} type="button" className="bo-pill" aria-pressed={f.categories.includes(c)} onClick={() => setF({ ...f, categories: f.categories.includes(c) ? f.categories.filter((x) => x !== c) : [...f.categories, c] })}>
              {shared(`categories.${c}.name`)}
            </button>
          ))}
        </div>
        <span className="bo-hint">{t('pr.forCatsHint')}</span>
      </div>
      <Field label={t('crm.note')}>
        <input className="bo-input" value={f.note} maxLength={300} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder={t('pr.promoNotePh')} />
      </Field>
      <label className="bo-check">
        <input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} />
        {t('pr.promoActive')}
      </label>
    </Dialog>
  );
}

// ───── релокация ─────

type RelItem = { id: string; kind: 'STEP' | 'DOC'; code: string; name: unknown; hint: unknown; active: boolean; inTurnkey: boolean };

function Relocation({ canEdit }: { canEdit: boolean }) {
  const { t, locale } = useT();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const toast = useToast();
  const list = useQuery(trpc.bo.products.relocation.queryOptions());
  const move = useMutation(trpc.bo.products.moveRelocation.mutationOptions({ onSuccess: () => qc.invalidateQueries({ queryKey: trpc.bo.products.relocation.queryKey() }), onError: (e) => toast(errText(t, e), 'error') }));
  const [edit, setEdit] = useState<{ kind: 'STEP' | 'DOC'; item?: RelItem } | null>(null);
  const name = (v: unknown) => {
    const o = (v ?? {}) as Record<string, string>;
    return o[locale] || o.ru || '';
  };
  return (
    <div className="bo-client-grid">
      {(['STEP', 'DOC'] as const).map((kind) => (
        <section key={kind} className="bo-panel">
          <div className="bo-panel__head">
            <h2 className="bo-h2">{t(`pr.rel.${kind}`)}</h2>
            {canEdit && (
              <button type="button" className="bo-btn bo-btn--ghost bo-btn--sm" onClick={() => setEdit({ kind })}>
                <I name="plus" size={16} />
              </button>
            )}
          </div>
          <ul className="bo-list">
            {(list.data ?? [])
              .filter((x) => x.kind === kind)
              .map((x) => (
                <li key={x.id} style={{ opacity: x.active ? 1 : 0.5 }}>
                  {canEdit && (
                    <span className="bo-stages__arrows">
                      <button type="button" onClick={() => move.mutate({ id: x.id, dir: -1 })} aria-label="↑">
                        <I name="arrowUp" size={14} />
                      </button>
                      <button type="button" onClick={() => move.mutate({ id: x.id, dir: 1 })} aria-label="↓">
                        <I name="arrowDown" size={14} />
                      </button>
                    </span>
                  )}
                  <span className="bo-grow" style={{ whiteSpace: 'normal' }}>
                    {name(x.name)}
                    {kind === 'STEP' && x.inTurnkey && <span className="bo-chip" style={{ marginLeft: 8 }}>{t('pr.turnkey')}</span>}
                  </span>
                  {canEdit && (
                    <button type="button" className="bo-btn bo-btn--ghost bo-btn--sm" onClick={() => setEdit({ kind, item: x as RelItem })}>
                      {t('prv.edit')}
                    </button>
                  )}
                </li>
              ))}
          </ul>
          <p className="bo-hint" style={{ marginTop: 10 }}>
            {t(`pr.relHint.${kind}`)}
          </p>
        </section>
      ))}
      {edit && <RelocationDialog kind={edit.kind} item={edit.item} onClose={() => setEdit(null)} />}
    </div>
  );
}

function RelocationDialog({ kind, item, onClose }: { kind: 'STEP' | 'DOC'; item?: RelItem; onClose: () => void }) {
  const { t } = useT();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState<L5>({ ...EMPTY, ...((item?.name as Partial<L5>) ?? {}) });
  const [hint, setHint] = useState<L5>({ ...EMPTY, ...((item?.hint as Partial<L5>) ?? {}) });
  const [active, setActive] = useState(item?.active ?? true);
  const [turnkey, setTurnkey] = useState(item?.inTurnkey ?? true);
  const save = useMutation(
    trpc.bo.products.saveRelocation.mutationOptions({
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: trpc.bo.products.relocation.queryKey() });
        toast(t('prv.saved'));
        onClose();
      },
      onError: (e) => toast(errText(t, e), 'error'),
    }),
  );
  return (
    <Dialog
      open
      wide
      onClose={onClose}
      title={t(`pr.rel.${kind}`)}
      footer={
        <>
          <button type="button" className="bo-btn bo-btn--ghost" onClick={onClose}>
            {t('ui.close')}
          </button>
          <button type="button" className="bo-btn" disabled={!name.ru.trim() || save.isPending} onClick={() => save.mutate({ id: item?.id, kind, name, hint: kind === 'DOC' ? hint : null, active, inTurnkey: kind === 'STEP' ? turnkey : false })}>
            {t('ui.save')}
          </button>
        </>
      }
    >
      <L5Editor label={t('pr.name')} value={name} defaults={{}} onChange={setName} />
      {kind === 'DOC' && <L5Editor label={t('pr.docHint')} value={hint} defaults={{}} onChange={setHint} />}
      {kind === 'STEP' && (
        <label className="bo-check">
          <input type="checkbox" checked={turnkey} onChange={(e) => setTurnkey(e.target.checked)} />
          {t('pr.turnkeyLabel')}
        </label>
      )}
      <label className="bo-check">
        <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
        {t('pr.relActive')}
      </label>
    </Dialog>
  );
}

// ───── новая услуга ─────

const NEW_ROLES = ['CLEANER', 'DRIVER', 'CAREER_CONSULTANT', 'DOCS_AGENT'] as const;

function NewServiceDialog({ catalog, onClose }: { catalog: Catalog; onClose: () => void }) {
  const { t, shared } = useT();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const toast = useToast();
  const [categoryId, setCategoryId] = useState(catalog[0]?.id ?? '');
  const [mode, setMode] = useState<'new' | 'copy'>('new');
  const [copyFromId, setCopyFrom] = useState(catalog[0]?.services[0]?.id ?? '');
  const [role, setRole] = useState<(typeof NEW_ROLES)[number]>('CLEANER');
  const [isOnline, setOnline] = useState(false);
  const [price, setPrice] = useState('10000');
  const [minutes, setMinutes] = useState('60');
  const [name, setName] = useState<L5>({ ...EMPTY });
  const [desc, setDesc] = useState<L5>({ ...EMPTY });
  const create = useMutation(
    trpc.bo.products.createService.mutationOptions({
      onSuccess: async (r) => {
        await loadTextOverrides();
        qc.invalidateQueries({ queryKey: trpc.bo.products.catalog.queryKey() });
        toast(t('pr.created'));
        onClose();
        navigate(`/products/${r.id}`);
      },
      onError: (e) => toast(errText(t, e), 'error'),
    }),
  );
  const all = catalog.flatMap((c) => c.services);
  return (
    <Dialog
      open
      wide
      onClose={onClose}
      title={t('pr.newService')}
      footer={
        <>
          <button type="button" className="bo-btn bo-btn--ghost" onClick={onClose}>
            {t('ui.close')}
          </button>
          <button
            type="button"
            className="bo-btn bo-btn--accent"
            disabled={!name.ru.trim() || !categoryId || create.isPending}
            onClick={() =>
              create.mutate({
                categoryId,
                name,
                description: desc,
                ...(mode === 'copy' ? { copyFromId } : { providerRole: role, isOnline, price: Number(price) || 0, minutes: Math.max(15, Number(minutes) || 60) }),
              })
            }
          >
            {t('pr.create')}
          </button>
        </>
      }
    >
      <div className="bo-chtabs">
        <button type="button" aria-pressed={mode === 'new'} onClick={() => setMode('new')}>
          {t('pr.fromScratch')}
        </button>
        <button type="button" aria-pressed={mode === 'copy'} onClick={() => setMode('copy')}>
          {t('pr.copyOf')}
        </button>
      </div>
      <Field label={t('pr.category')}>
        <select className="bo-select" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
          {catalog.map((c) => (
            <option key={c.id} value={c.id}>
              {shared(`categories.${c.code}.name`)}
            </option>
          ))}
        </select>
      </Field>
      {mode === 'copy' ? (
        <Field label={t('pr.copyFrom')} hint={t('pr.copyHint')}>
          <select className="bo-select" value={copyFromId} onChange={(e) => setCopyFrom(e.target.value)}>
            {all.map((x) => (
              <option key={x.id} value={x.id}>
                {shared(`services.${x.code}.name`)}
              </option>
            ))}
          </select>
        </Field>
      ) : (
        <>
          <div>
            <span className="bo-label">{t('pr.who')}</span>
            <div className="bo-roles" style={{ marginTop: 6 }}>
              {NEW_ROLES.map((r) => (
                <button key={r} type="button" className="bo-role" aria-pressed={role === r} onClick={() => setRole(r)}>
                  <b>{shared(`roles.${r}`)}</b>
                  <span className="bo-small">{t(`pr.flowHint.${r}`)}</span>
                </button>
              ))}
            </div>
            <span className="bo-hint">{t('pr.whoHint')}</span>
          </div>
          <div className="bo-row2">
            <Field label={t('pr.fixedPrice')}>
              <input className="bo-input" inputMode="numeric" value={price} onChange={(e) => setPrice(num(e.target.value))} />
            </Field>
            <Field label={t('pr.minutes')}>
              <input className="bo-input" inputMode="numeric" value={minutes} onChange={(e) => setMinutes(num(e.target.value))} />
            </Field>
          </div>
          <label className="bo-check">
            <input type="checkbox" checked={isOnline} onChange={(e) => setOnline(e.target.checked)} />
            {t('pr.online')}
          </label>
        </>
      )}
      <L5Editor label={t('pr.name')} value={name} defaults={{ ru: t('pr.namePh') }} onChange={setName} />
      <L5Editor label={t('pr.description')} value={desc} defaults={{}} onChange={setDesc} multiline />
      <p className="bo-hint">{t('pr.createHint')}</p>
    </Dialog>
  );
}

type QField = { key?: string; type: 'select' | 'int' | 'text' | 'bool'; label: string; required: boolean; options: { key?: string; label: string }[]; min: number; max: number };

/** Вопросы клиенту при заказе — для услуг с фиксированной ценой */
function ParamsPanel({ service: s, canEdit }: { service: Service; canEdit: boolean }) {
  const { t, shared } = useT();
  const trpc = useTRPC();
  const label = (k: string) => {
    const v = shared(k);
    return v === k ? '' : v;
  };
  const initial = (): QField[] =>
    s.paramsSchema.fields
      .filter((f) => f.type !== 'extras')
      .map((f) => ({
        key: f.key,
        type: f.type as QField['type'],
        label: label(`params.${s.code}.${f.key}.label`) || f.key,
        required: 'required' in f ? !!f.required : false,
        options: f.type === 'select' ? f.options.map((o) => ({ key: o, label: label(`params.${s.code}.${f.key}.${o}`) || o })) : [],
        min: f.type === 'int' ? f.min : 1,
        max: f.type === 'int' ? f.max : 10,
      }));
  const [fields, setFields] = useState<QField[]>(initial);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setFields(initial()), [s.paramsSchema]);
  const save = useMutation(trpc.bo.products.saveParams.mutationOptions(useSaved()));
  const set = (i: number, patch: Partial<QField>) => setFields(fields.map((f, k) => (k === i ? { ...f, ...patch } : f)));
  return (
    <section className="bo-panel">
      <div className="bo-panel__head">
        <h2 className="bo-h2">{t('pr.questions')}</h2>
      </div>
      <p className="bo-hint" style={{ marginBottom: 12 }}>
        {t('pr.questionsHint')}
      </p>
      <div className="bo-col" style={{ gap: 10 }}>
        {fields.map((f, i) => (
          <div key={f.key ?? `n${i}`} className="bo-qfield">
            <div style={{ display: 'flex', gap: 8 }}>
              <input className="bo-input" disabled={!canEdit} value={f.label} maxLength={120} onChange={(e) => set(i, { label: e.target.value })} placeholder={t('pr.qLabel')} />
              <select
                className="bo-select"
                style={{ width: 160 }}
                disabled={!canEdit}
                value={f.type}
                onChange={(e) => set(i, { type: e.target.value as QField['type'], options: e.target.value === 'select' && f.options.length < 2 ? [{ label: '' }, { label: '' }] : f.options })}
              >
                {(['select', 'int', 'text', 'bool'] as const).map((x) => (
                  <option key={x} value={x}>
                    {t(`pr.qType.${x}`)}
                  </option>
                ))}
              </select>
              {canEdit && (
                <button type="button" className="bo-iconbtn" onClick={() => setFields(fields.filter((_, k) => k !== i))} aria-label={t('chat.tplDelete')}>
                  <I name="close" size={16} />
                </button>
              )}
            </div>
            {f.type === 'select' && (
              <div className="bo-col" style={{ gap: 6, marginTop: 8, paddingLeft: 12 }}>
                {f.options.map((o, k) => (
                  <div key={o.key ?? `o${k}`} style={{ display: 'flex', gap: 6 }}>
                    <input
                      className="bo-input"
                      style={{ minHeight: 34, padding: '4px 10px' }}
                      disabled={!canEdit}
                      value={o.label}
                      maxLength={120}
                      placeholder={t('pr.qOption', { n: k + 1 })}
                      onChange={(e) => set(i, { options: f.options.map((x, j) => (j === k ? { ...x, label: e.target.value } : x)) })}
                    />
                    {canEdit && f.options.length > 2 && (
                      <button type="button" className="bo-iconbtn" onClick={() => set(i, { options: f.options.filter((_, j) => j !== k) })} aria-label={t('chat.tplDelete')}>
                        <I name="close" size={14} />
                      </button>
                    )}
                  </div>
                ))}
                {canEdit && (
                  <button type="button" className="bo-side__link" style={{ color: 'var(--bc-accent)', padding: 0, alignSelf: 'flex-start' }} onClick={() => set(i, { options: [...f.options, { label: '' }] })}>
                    + {t('pr.qAddOption')}
                  </button>
                )}
              </div>
            )}
            {f.type === 'int' && (
              <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }} className="bo-small">
                {t('pr.qFrom')}
                <input className="bo-input" style={{ width: 80, minHeight: 34 }} disabled={!canEdit} inputMode="numeric" value={String(f.min)} onChange={(e) => set(i, { min: Number(num(e.target.value)) || 0 })} />
                {t('pr.qTo')}
                <input className="bo-input" style={{ width: 80, minHeight: 34 }} disabled={!canEdit} inputMode="numeric" value={String(f.max)} onChange={(e) => set(i, { max: Number(num(e.target.value)) || 1 })} />
              </div>
            )}
            {f.type !== 'bool' && (
              <label className="bo-check bo-small" style={{ marginTop: 6 }}>
                <input type="checkbox" disabled={!canEdit} checked={f.required} onChange={(e) => set(i, { required: e.target.checked })} />
                {t('pr.qRequired')}
              </label>
            )}
          </div>
        ))}
        {fields.length === 0 && <p className="bo-hint">{t('pr.qNone')}</p>}
      </div>
      {canEdit && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          <button type="button" className="bo-btn bo-btn--ghost bo-btn--sm" onClick={() => setFields([...fields, { type: 'select', label: '', required: true, options: [{ label: '' }, { label: '' }], min: 1, max: 10 }])}>
            <I name="plus" size={16} />
            {t('pr.qAdd')}
          </button>
          <button
            type="button"
            className="bo-btn bo-btn--sm"
            disabled={save.isPending || fields.some((f) => !f.label.trim() || (f.type === 'select' && f.options.filter((o) => o.label.trim()).length < 2))}
            onClick={() => save.mutate({ serviceId: s.id, fields: fields.map((f) => ({ ...f, options: f.type === 'select' ? f.options.filter((o) => o.label.trim()) : [] })) })}
          >
            {t('ui.save')}
          </button>
        </div>
      )}
    </section>
  );
}

function DeleteService({ id }: { id: string }) {
  const { t } = useT();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const toast = useToast();
  const del = useMutation(
    trpc.bo.products.deleteService.mutationOptions({
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: trpc.bo.products.catalog.queryKey() });
        navigate('/products');
      },
      onError: (e) => toast(errText(t, e), 'error'),
    }),
  );
  return (
    <button type="button" className="bo-side__link" style={{ color: 'var(--bc-danger)', marginTop: 16 }} onClick={() => confirm(t('pr.deleteConfirm')) && del.mutate({ id })}>
      {t('pr.delete')}
    </button>
  );
}
